// Tutoriales in-app (#1013, #1035) — decisiones puras: qué tutorial toca en qué
// pantalla y si se puede mostrar a este usuario. Sin React ni red: se prueban
// en eligibility.test.ts.
import type { Tutorial, TutorialProgress, TutorialRules } from "./types";

/** ¿La plataforma deja ver este tutorial a este usuario? (banderas) */
export function isAllowed(id: string, rules: TutorialRules | null): boolean {
  // Sin respuesta del backend no se muestra nada: un tutorial que aparece de
  // más molesta; uno que tarda un segundo en aparecer, no.
  if (!rules || !rules.active) return false;
  if (rules.blocked.includes(id)) return false;
  if (rules.allowedOnly && !rules.allowedOnly.includes(id)) return false;
  return true;
}

/** Banderas + herramientas internas (sólo administradores de plataforma). */
export function canSee(tutorial: Tutorial, rules: TutorialRules | null): boolean {
  if (!isAllowed(tutorial.id, rules)) return false;
  if (tutorial.internal && !rules?.platformAdmin) return false;
  return true;
}

/** ¿Se abre SOLO al entrar a la pantalla? (banderas + "no volver a mostrar") */
export function shouldAutoStart(
  tutorial: Tutorial,
  rules: TutorialRules | null,
  progress: TutorialProgress | null,
): boolean {
  if (!canSee(tutorial, rules)) return false;
  if (!progress) return false;
  if (progress.dontShowAny) return false;
  if (progress.dismissed[tutorial.id]) return false;
  if (progress.completed[tutorial.id]) return false;
  return true;
}

/** ¿`path` cae en `prefix`? ("/crm" cubre "/crm/panel" pero no "/crmx"). */
export function pathMatches(path: string, prefix: string): boolean {
  if (!prefix) return false;
  const clean = prefix.endsWith("/") && prefix.length > 1 ? prefix.slice(0, -1) : prefix;
  return path === clean || path.startsWith(`${clean}/`);
}

/**
 * Tutoriales de la pantalla actual: los de prefijo más largo que coincida (en
 * una misma pantalla pueden vivir varias funcionalidades, p. ej. la
 * configuración de la mesa de servicio), en el orden del catálogo.
 */
export function tutorialsForPath(path: string, catalog: Tutorial[]): Tutorial[] {
  let bestLength = -1;
  let best: Tutorial[] = [];
  for (const tutorial of catalog) {
    const length = Math.max(-1, ...tutorial.routes.filter((r) => pathMatches(path, r)).map((r) => r.length));
    if (length < 0) continue;
    if (length > bestLength) {
      bestLength = length;
      best = [tutorial];
    } else if (length === bestLength) {
      best.push(tutorial);
    }
  }
  return best;
}

/** El primero de la pantalla (compatibilidad). */
export function tutorialForPath(path: string, catalog: Tutorial[]): Tutorial | null {
  return tutorialsForPath(path, catalog)[0] ?? null;
}

/** Tutoriales que el usuario puede abrir a mano desde Ayuda. */
export function availableTutorials(catalog: Tutorial[], rules: TutorialRules | null): Tutorial[] {
  return catalog.filter((t) => canSee(t, rules));
}

/** "<categoria>/<slug>" → ruta interna de la wiki en la app Ayuda. */
export function wikiRoute(wiki: string | undefined): string | null {
  if (!wiki || !/^[a-z0-9-]+\/[a-z0-9-]+$/.test(wiki)) return null;
  return `/ayuda/wiki/${wiki}`;
}
